import { Link, router, usePage } from '@inertiajs/react';
import { useEffect } from 'react';
import {
  Calendar,
  CalendarClock,
  Clock,
  DoorOpen,
  FolderKanban,
  Gamepad2,
  Gavel,
  HandCoins,
  IdCard,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Mic,
  Music,
  Shield,
  Sparkles,
  Target,
  UserCheck,
  UserPlus,
  Users,
  UsersRound,
  Utensils,
} from 'lucide-react';
import { SidebarUserNav } from '@/components/sidebar-user-nav';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import type { AdminShellEvent, CurrentUser } from '@/types';

// Mirrors AdminRoutes::EVENT_SEGMENTS (app/lib/admin_routes.rb): the segments that live
// under an edition, used to keep the current section when switching editions.
const ADMIN_EVENT_ROUTE_SEGMENTS = [
  'dashboard',
  'review',
  'entrance',
  'credentials',
  'teams',
  'projects',
  'tracks',
  'mentors',
  'judging',
  'arcade',
  'time-slots',
  'external-people',
  'sponsors',
  'reviewers',
  'market',
  'stages',
  'dinner',
  'music',
  'feedback',
  'cron',
] as const;

type AdminEventRouteSegment = (typeof ADMIN_EVENT_ROUTE_SEGMENTS)[number];

interface AdminSidebarProps {
  events: AdminShellEvent[];
  user: CurrentUser;
  defaultEventSlug: string | null;
  activeEventSlug: string | null;
}

const eventMenuItems: Array<{
  title: string;
  segment: AdminEventRouteSegment;
  icon: typeof LayoutDashboard;
  fullAccessOnly?: boolean;
}> = [
  {
    title: 'Dashboard',
    segment: 'dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Review',
    segment: 'review',
    icon: Users,
  },
  {
    title: 'Reviewers',
    segment: 'reviewers',
    icon: Shield,
    fullAccessOnly: true,
  },
  {
    title: 'Entrance',
    segment: 'entrance',
    icon: DoorOpen,
  },
  {
    title: 'Credentials',
    segment: 'credentials',
    icon: IdCard,
  },
  {
    title: 'Teams',
    segment: 'teams',
    icon: UsersRound,
    fullAccessOnly: true,
  },
  {
    title: 'Projects',
    segment: 'projects',
    icon: FolderKanban,
  },
  {
    title: 'Tracks',
    segment: 'tracks',
    icon: Target,
    fullAccessOnly: true,
  },
  {
    title: 'Mentors',
    segment: 'mentors',
    icon: UserCheck,
    fullAccessOnly: true,
  },
  {
    title: 'Judging',
    segment: 'judging',
    icon: Gavel,
    fullAccessOnly: true,
  },
  {
    title: 'External People',
    segment: 'external-people',
    icon: UserPlus,
    fullAccessOnly: true,
  },
  {
    title: 'Sponsors',
    segment: 'sponsors',
    icon: HandCoins,
    fullAccessOnly: true,
  },
  {
    title: 'Arcade Games',
    segment: 'arcade',
    icon: Gamepad2,
  },
  {
    title: 'Time Slots',
    segment: 'time-slots',
    icon: CalendarClock,
    fullAccessOnly: true,
  },
  {
    title: 'Market',
    segment: 'market',
    icon: Sparkles,
    fullAccessOnly: true,
  },
  {
    title: 'Stages',
    segment: 'stages',
    icon: Mic,
    fullAccessOnly: true,
  },
  {
    title: 'Dinner',
    segment: 'dinner',
    icon: Utensils,
    fullAccessOnly: true,
  },
  {
    title: 'Music',
    segment: 'music',
    icon: Music,
    fullAccessOnly: true,
  },
  {
    title: 'Feedback',
    segment: 'feedback',
    icon: MessageSquare,
    fullAccessOnly: true,
  },
  {
    title: 'Cron Jobs',
    segment: 'cron',
    icon: Clock,
    fullAccessOnly: true,
  },
];

const generalMenuItems = [
  {
    title: 'Events',
    href: '/admin/events',
    icon: Calendar,
    fullAccessOnly: true,
  },
  {
    title: 'Arcade Challenges',
    href: '/admin/arcade-challenges',
    icon: Gamepad2,
    fullAccessOnly: true,
  },
  {
    title: 'Emails',
    href: '/admin/emails',
    icon: Mail,
  },
  {
    title: 'Cron Jobs',
    href: '/admin/cron',
    icon: Clock,
    fullAccessOnly: true,
  },
  {
    title: 'Users',
    href: '/admin/users',
    icon: Shield,
    fullAccessOnly: true,
  },
];

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

// `usePage().url` is the path plus its query string; the Next component read the two
// halves from `usePathname()` and `useSearchParams()`.
function useCurrentUrl() {
  const url = usePage().url;
  const [pathname, search = ''] = url.split('?');

  return { pathname, search };
}

function EventSwitcher({
  activeEventSlug,
  events,
}: {
  activeEventSlug: string;
  events: AdminShellEvent[];
}) {
  const { pathname, search } = useCurrentUrl();

  const handleEventChange = (eventSlug: string) => {
    const pathSegments = pathname.split('/').filter(Boolean);
    const currentSegment = pathSegments[2];
    const targetSegment = ADMIN_EVENT_ROUTE_SEGMENTS.includes(
      currentSegment as AdminEventRouteSegment,
    )
      ? currentSegment
      : 'dashboard';
    const targetPath = `/admin/${eventSlug}/${targetSegment}`;
    const isEventListPage = pathSegments.length === 3;
    const queryString = isEventListPage ? search : '';

    router.visit(queryString ? `${targetPath}?${queryString}` : targetPath);
  };

  return (
    <Select value={activeEventSlug} onValueChange={handleEventChange}>
      <SelectTrigger className="h-8 w-full text-xs">
        <SelectValue placeholder="Select event" />
      </SelectTrigger>
      <SelectContent>
        {events.map((event) => (
          <SelectItem key={event.id} value={event.slug}>
            {event.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AdminSidebarContent({
  events,
  user,
  defaultEventSlug,
  activeEventSlug: activeEventSlugProp,
}: AdminSidebarProps) {
  const { pathname } = useCurrentUrl();
  const { state, setOpenMobile } = useSidebar();
  const isFullAccess = user.adminRole === 'full';

  // On mobile the sidebar is a Sheet drawer sitting on top of the page; without this
  // it stays open over the new page after a nav link is tapped, since Inertia swaps
  // the page component in place rather than unmounting this layout.
  useEffect(() => router.on('navigate', () => setOpenMobile(false)), [setOpenMobile]);
  const activeEventSlug =
    activeEventSlugProp ?? defaultEventSlug ?? events[0]?.slug ?? null;

  const visibleEventMenuItems = activeEventSlug
    ? eventMenuItems
        .filter((item) => !item.fullAccessOnly || isFullAccess)
        .map((item) => ({
          ...item,
          href: `/admin/${activeEventSlug}/${item.segment}`,
        }))
    : [];
  const visibleGeneralMenuItems = generalMenuItems.filter(
    (item) => !item.fullAccessOnly || isFullAccess,
  );

  return (
    <>
      <SidebarHeader>
        <div className="flex items-start justify-between gap-2 px-2 py-2">
          {state === 'expanded' ? (
            <>
              <div className="flex flex-1 flex-col gap-2 px-2">
                <div className="flex flex-col gap-0.5">
                  <h2 className="font-semibold text-lg">Platanus Hack Admin</h2>
                  <p className="text-muted-foreground text-xs">Admin Panel</p>
                </div>
                {activeEventSlug && events.length > 0 && (
                  <EventSwitcher
                    activeEventSlug={activeEventSlug}
                    events={events}
                  />
                )}
              </div>
              <SidebarTrigger />
            </>
          ) : (
            <SidebarTrigger />
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {visibleEventMenuItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Event</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleEventMenuItems.map((item) => {
                  const Icon = item.icon;

                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActivePath(pathname, item.href)}
                        tooltip={item.title}
                      >
                        <Link href={item.href}>
                          <Icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>General</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleGeneralMenuItems.map((item) => {
                const Icon = item.icon;

                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActivePath(pathname, item.href)}
                      tooltip={item.title}
                    >
                      <Link href={item.href}>
                        <Icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarUserNav user={user} />
      </SidebarFooter>
    </>
  );
}

export function AdminSidebar(props: AdminSidebarProps) {
  return (
    <Sidebar collapsible="icon">
      <AdminSidebarContent {...props} />
    </Sidebar>
  );
}
